import React, { useEffect, useRef } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { logger } from 'utils/logging'
import { purgeAllDrafts } from 'utils/purchaseFlowDrafts'
import { PfDraftsQueryKeys, useDeleteDraft } from 'dataQueries/purchaseFlowDrafts.query'
import { Billing, Catalogue, Instructions, ProductConfiguration, PropertyFirstEntry, Summary } from './routing'
import { setPfFlowVersion } from 'utils/analytics'
import { PF_FLOW_VERSION } from './analytics'
import { resetFlowLoadedGate } from './routing/flowLoadedGate'
import { resetSizeAnnouncementGate } from './routing/sizeAnnouncementGate'
import { EmbedOverlayEntry } from './entry/EmbedOverlayEntry.component'
import LoadingPage from 'components/pages/General/LoadingPage/LoadingPage'
import { PF_SESSION_SEGMENT, PfStepPath, usePfPath } from './routing/pfBasePath'
import { PfSessionRestore } from './routing/PfSessionRestore'
import { usePfSessionUrl } from './routing/usePfSessionUrl'
import { PfRevampFlagGate } from './routing/PfRevampFlagGate'
import { PfOrderAttemptIdProvider } from './routing/pfOrderAttemptId'
import { usePfEmbed } from './routing/usePfEmbed'
import { usePfOrderFlowProperty } from './routing/usePfOrderFlowProperty'
import { PfEmbedConsentProvider, usePfEmbedConsentState } from './routing/usePfEmbedConsent'
import { useEnsurePfV2Session } from './routing/useEnsurePfV2Session'
import { useRecoverRejectedPfV2Session } from './routing/useRecoverRejectedPfV2Session'
import { useStripeReturn } from './routing/useStripeReturn'
import { forgetEmbedLocale } from './routing/embedLocale'
import { forgetEmbedPropertyRef } from './routing/embedPropertyRef'
import { EmbedOrderConfirmation } from 'components/common/Embed/EmbedOrderConfirmation'
import { EmbedPaymentFailed } from './components/EmbedPaymentFailed.component'
import { usePfAutoSave, usePfStore } from './store'
import { useOneSystemPricingSync } from './store/useOneSystemPricingSync'
import { useInstructionFeesMirror } from './steps/instructions/useInstructionFeesMirror'
import { usePropertyAreaPrefills } from './modules/usePropertyAreaPrefill'
import { useFloorPlanPropertyUsageSync } from './modules/floorPlanConfig/useFloorPlanPropertyUsageSync'
import { emptyDraftToDiscard } from './drafts/emptyDraftOnLeave'
import { MeasurementQuoteSyncs } from './modules/measurement/MeasurementQuoteSyncs'
import { WorkspaceDiscountProvider } from './components/WorkspaceDiscount.provider'
import { PfV2AdditionalInfoUploadProvider, PfV2FloorPlanLogoProvider, PfV2PoaUploadProvider, PfV2VisualUploadProvider, ResumedUploads, SessionDocumentReconcile, ShootOutputUploadCleanup } from './fileHub'

/**
 * Sentinel key that records when the one-shot draft wipe ran on this browser.
 * Intentionally uses the `pf-drafts-rollout:` prefix (not `pf-drafts:`) so
 * that `purgeAllDrafts`, which targets the `pf-drafts:` prefix, can never
 * accidentally delete its own guard.
 */
const DRAFTS_WIPE_SENTINEL_KEY = 'pf-drafts-rollout:wiped-2026-04-30'

/**
 * Root page for the revamped Purchase Flow.
 *
 * Owns all sub-routes under `/purchase-flow-revamp/`:
 *   /select-user                — Legacy admin-gate URL, redirects to select-property
 *   /select-property            — Variant A property-first entry (Module 5)
 *   /catalogue                  — Variant B + catalogue browse (Module 6)
 *   /configuration/:cartItemId  — Product configuration step (Module 9, Phase 3)
 *   /instructions               — Instructions step (Phase 3+)
 *
 * When `ALLOW_PF_REVAMP` is off, `PfRevampFlagGate` redirects to the legacy purchase flow.
 * Admin-on-behalf client selection is no longer a page: it is the first stage
 * of `PropertySelectionDrawer`, so every role enters on `/select-property`.
 */
const PurchaseFlowRevampContent: React.FC = () => {
  const { isEmbed } = usePfEmbed()
  usePfOrderFlowProperty()
  const orderPlaced = usePfStore((s) => s.orderPlaced)
  const { isConsentResolved, showValueProposition, dismissValueProposition } = usePfEmbedConsentState()

  const queryClient = useQueryClient()

  // One-shot rollout cleanup: wipe all pf-drafts:* localStorage keys on the
  // first PF revamp page mount per browser. Pre-existing drafts may carry
  // items with no property selected, which becomes unreachable state under the
  // new property-completeness gate. Tech lead approved a clean break.
  // The sentinel key lives outside the pf-drafts: namespace so purgeAllDrafts
  // can never remove it, making this effect exactly-once per browser.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.localStorage.getItem(DRAFTS_WIPE_SENTINEL_KEY)) return

    try {
      const removed = purgeAllDrafts()
      window.localStorage.setItem(DRAFTS_WIPE_SENTINEL_KEY, new Date().toISOString())

      queryClient.invalidateQueries({ queryKey: [PfDraftsQueryKeys.DRAFTS_LIST] })
      queryClient.removeQueries({ queryKey: [PfDraftsQueryKeys.DRAFT] })

      logger.info('PF revamp rollout: purged stale localStorage drafts', undefined, {
        context: {
          component: 'PurchaseFlowRevamp',
          action: 'purgeAllDrafts',
          metadata: { removed },
        },
      })
    } catch (err) {
      logger.error('PF revamp rollout: failed to purge localStorage drafts', err, {
        context: {
          component: 'PurchaseFlowRevamp',
          action: 'purgeAllDrafts',
        },
      })
    }
  }, [queryClient])

  // Establish the PF v2 purchase session once a property is selected (Slice C).
  // The sessionId is stored in draftSlice and flows onto the catalogue request
  // + the order placement body. The hook self-gates on `property?.id` (and, for
  // admin, on the target email/workspace), so mounting it at the shell root —
  // before a property exists — is a no-op until the user picks one.
  const adminTargetEmail = usePfStore((s) => s.targetUserEmail)

  // Instructions deliberately SURVIVE a cart edit (product decision 2026-08-25,
  // reversing DF-I17's legacy port). They are cleared only when a new order
  // starts — which the unmount-time `resetPersistedState` below already does,
  // since every PF route is header-less so "start new order" can only be
  // reached from outside the flow — and otherwise come from the draft or
  // survive navigation between steps.
  useEnsurePfV2Session({ targetEmail: adminTargetEmail })
  // …and replaces it if the backend turns out not to have it.
  useRecoverRejectedPfV2Session()
  usePfSessionUrl()

  // Mount the autosave subscription at page level so a single subscription
  // covers the entire PF revamp lifetime. Mounting per-step would re-subscribe
  // on every route transition and reset the 800ms debounce window mid-edit.
  usePfAutoSave()
  useOneSystemPricingSync()
  useInstructionFeesMirror()
  usePropertyAreaPrefills()
  useFloorPlanPropertyUsageSync()

  // Phase 1.5 — purge Zustand state on PF leave.
  // When the user navigates away from /purchase-flow-revamp/*, this component
  // unmounts. Reset all persisted slices so stale cart / property / billing
  // data doesn't resurface on the next PF entry within the same tab.
  // sessionId is intentionally preserved — useEnsurePfV2Session owns its lifecycle
  // (it regenerates a fresh v2 session for the selected property on the next entry).
  // Marks every analytics event fired while this flow is mounted as v2's,
  // including the ones shared components fire on their own. Set during RENDER
  // rather than in an effect: children's effects run before the parent's, so an
  // effect here would miss whatever the first screen fires on mount.
  setPfFlowVersion(PF_FLOW_VERSION)

  // Becka's launcher sits where this flow keeps its primary actions; styles/becka.css hides it.
  useEffect(() => {
    document.body.dataset.pfRevampOpen = 'true'
    return () => { delete document.body.dataset.pfRevampOpen }
  }, [])

  const { mutate: deleteDraft } = useDeleteDraft()
  const deleteDraftRef = useRef(deleteDraft)
  deleteDraftRef.current = deleteDraft

  useEffect(() => {
    return () => {
      const emptyDraftId = emptyDraftToDiscard(usePfStore.getState())
      if (emptyDraftId) deleteDraftRef.current(emptyDraftId)
      usePfStore.getState().resetPersistedState()
      // Re-arms `flow_loaded`, so a second visit in the same tab counts as a
      // second entry into the funnel rather than disappearing from it.
      resetFlowLoadedGate()
      resetSizeAnnouncementGate()
      // Stop marking: from here on the client is somewhere else in the app.
      setPfFlowVersion(null)
      // The forced ?lang and the CRM's property belong to this iframe session only.
      forgetEmbedLocale()
      forgetEmbedPropertyRef()
    }
  }, [])

  const { isEmbedPaymentFailed } = useStripeReturn(isEmbed)

  // Terminal embed state: the order is placed and the client must not be navigated out of the
  // iframe. The same component legacy renders — it is presentation plus the shared
  // `useEmbedClose`, so both flows offer the identical close and countdown rather than two of them.
  if (isEmbed && orderPlaced) return <EmbedOrderConfirmation />
  if (isEmbedPaymentFailed) return <EmbedPaymentFailed />

  // Inside the embed, consent decides the first screen: hold the flow until the
  // status has settled rather than flashing the catalogue behind the overlay.
  if (!isConsentResolved) return <LoadingPage />

  // The overlay replaces the flow rather than covering it — per the prototype the
  // service cards are the only way forward. The picked service is not carried into
  // the catalogue yet (v2 has no category-hint concept); dismissing is what it does.
  if (showValueProposition) return <EmbedOverlayEntry onSelect={dismissValueProposition} />

  return (
    <PfV2VisualUploadProvider>
    {/* Puts a resumed line's already-uploaded files back on screen. Must sit
        inside the visual-upload provider, which owns that FileAPI scope. */}
    <ResumedUploads />
    <ShootOutputUploadCleanup />
    <MeasurementQuoteSyncs />
    <PfV2PoaUploadProvider>
    <PfV2AdditionalInfoUploadProvider>
    <SessionDocumentReconcile />
    <PfV2FloorPlanLogoProvider>
    <WorkspaceDiscountProvider>

    <Routes>
      {/** A reload of a session URL restores that session's order before any step renders. */}
      <Route path={`${PF_SESSION_SEGMENT}/:sessionId/*`} element={<PfSessionRestore>{stepRoutes}</PfSessionRestore>} />
      <Route path="*" element={stepRoutes} />
    </Routes>

    </WorkspaceDiscountProvider>
    </PfV2FloorPlanLogoProvider>
    </PfV2AdditionalInfoUploadProvider>
    </PfV2PoaUploadProvider>
    </PfV2VisualUploadProvider>
  )
}

const PfStepRoutes: React.FC = () => {
  // Every in-flow redirect resolves against the base the client entered on, so
  // the same route table serves the flow's own URL and the Propstack embed's.
  const pfPath = usePfPath()
  // The order is placed and only its refused payment is open: anything edited now would never reach the order.
  const isPaymentPending = usePfStore((s) => s.paymentFailure !== null)

  if (isPaymentPending) {
    return (
      <Routes>
        <Route path="summary" element={<Summary />} />
        <Route path="*" element={<Navigate to={pfPath(PfStepPath.SUMMARY)} replace />} />
      </Routes>
    )
  }

  return (
    <Routes>

      {/** Former admin gate. The client-selection step now lives INSIDE
       *  PropertySelectionDrawer (a stage swap, `ClientSelectionSection`), so
       *  there is no page to render here — the route is kept only so existing
       *  deep links / bookmarks land somewhere sane. */}
      <Route
        path="select-user"
        element={<Navigate to={pfPath(PfStepPath.SELECT_PROPERTY)} replace />}
      />

      {/** Variant A — property-first entry */}
      <Route path="select-property" element={<PropertyFirstEntry />} />

      {/** Variant B + catalogue browse (Module 6) */}
      <Route path="catalogue" element={<Catalogue />} />

      {/** Module 9 — Product configuration step (Phase 3 Round 1; Phase A.5 multi-flow).
       *  The page itself owns the legacy `:cartItemId` (no `:flowId`) → first-flow
       *  redirect, so the same component handles both shapes. */}
      <Route path="configuration/:cartItemId" element={<ProductConfiguration />} />
      <Route path="configuration/:cartItemId/:flowId" element={<ProductConfiguration />} />

      {/** Every step reads and writes the Zustand slices directly and needs no
       *  legacy provider tree, so they are flat routes with no shared shell.
       *  Back-nav state survives because the store is a singleton (not
       *  remounted) — the old cross-step `LegacyStepShell` hoist, and the state
       *  bridges that fought its reset-on-remount, are gone. */}
      {/** Module 11 — Instructions step */}
      <Route path="instructions" element={<Instructions />} />

      {/** Module 12 — Billing step */}
      <Route path="billing" element={<Billing />} />

      {/** Module 13 — Summary step */}
      <Route path="summary" element={<Summary />} />

      {/** Root: everyone lands on property-first entry. Admins get the
       *  client-selection stage first, inside the property drawer. */}
      <Route
        path="/"
        element={<Navigate to={pfPath(PfStepPath.SELECT_PROPERTY)} replace />}
      />

      {/** Catch-all: redirect to root handler above */}
      <Route path="*" element={<Navigate to={pfPath()} replace />} />

    </Routes>
  )
}

const stepRoutes = <PfStepRoutes />

/**
 * Consent is one decision for the whole flow — the overlay, the Summary
 * checkbox and the Place Order gate all read the same instance — so the
 * provider sits outside the component that consumes it.
 */
export const PurchaseFlowRevamp: React.FC = () => (
  <PfRevampFlagGate>
    <PfOrderAttemptIdProvider>
      <PfEmbedConsentProvider>
        <PurchaseFlowRevampContent />
      </PfEmbedConsentProvider>
    </PfOrderAttemptIdProvider>
  </PfRevampFlagGate>
)
